import { equals, FilterExpression, isNotNull, isNull, lessThanOrEqual } from '@infuse/api/common';

import { Availability, ChargingStation, Status, SubStatus } from '@infuse/api/cpms/operator';
import { ConnectorIconType, UiTagVariant } from '@infuse/ui/domain';
import { EViolinStatus } from '@infuse/ui/utils';
import moment from 'moment';
import { ComponentStatus, ComponentSubStatus, Connector, ConnectorType, EvseStatus } from './model';
import { Feature, useFeatureStore } from '@infuse/cpms/shared/features/store';

/**
 * The action status of a Charging Station indicates whether an action by an operator is required.
 *
 * ActionRequired: an action is required due to unforeseen circumstances (e.g. hardware error, unexpected offline state)
 * ConfigurationRequired: an action is required due to regular business processes (e.g. incomplete commissioning)
 */
export enum ActionStatus {
  ACTION_REQUIRED = 'ActionRequired',
  CONFIGURATION_REQUIRED = 'ConfigurationRequired'
}

/**
 * 'Friendly status' is a status enum that combines multiple concepts that are
 * separate properties of different entities in the backend.
 * The friendly status itself is a property of the charge point.
 *
 * The friendly status is more refined than the action status. It can be seen as
 * a reason for why a charging station has a certain action status, if any.
 */
export enum FriendlyStatus {
  IDLE = 'Idle',
  IN_USE = 'InUse',
  OFFLINE = 'Offline',
  UNKNOWN = 'Unknown',
  NO_STATUS_RECEIVED = 'NoStatusReceived',
  FAULTED = 'Faulted',
  INOPERATIVE = 'Inoperative',
  CHARGING = 'Charging',
  SUSPENDED_EV = 'SUSPENDED_EV',
  AVAILABLE = 'Available'
}

export const FriendlyStatusColors: { [key: string]: UiTagVariant } = {
  [FriendlyStatus.IN_USE]: 'eVcharging',
  [FriendlyStatus.SUSPENDED_EV]: 'eVfullyCharged',
  [FriendlyStatus.OFFLINE]: 'warning',
  [FriendlyStatus.UNKNOWN]: 'warning',
  [FriendlyStatus.NO_STATUS_RECEIVED]: 'warning',
  [FriendlyStatus.FAULTED]: 'eVerror',
  [FriendlyStatus.INOPERATIVE]: 'eVinactive',
  [FriendlyStatus.IDLE]: 'eVinactive',
  [FriendlyStatus.AVAILABLE]: 'eVavailable',
  [FriendlyStatus.CHARGING]: 'eVcharging'
};

export const FriendlyStatusIcon: { [key: string]: [string, string] } = {
  [FriendlyStatus.IN_USE]: ['fal', 'bolt'],
  [FriendlyStatus.SUSPENDED_EV]: ['fal', 'bolt-slash'],
  [FriendlyStatus.OFFLINE]: ['fal', 'circle-xmark'],
  [FriendlyStatus.UNKNOWN]: ['fal', 'triangle-exclamation'],
  [FriendlyStatus.NO_STATUS_RECEIVED]: ['fal', 'triangle-exclamation'],
  [FriendlyStatus.FAULTED]: ['fal', 'xmark'],
  [FriendlyStatus.INOPERATIVE]: ['fal', 'wrench'],
  [FriendlyStatus.IDLE]: ['fal', 'hourglass-empty'],
  [FriendlyStatus.AVAILABLE]: ['fal', 'circle-check'],
  [FriendlyStatus.CHARGING]: ['fal', 'bolt']
};

export const determineActionStatusAndFriendlyStatus = (
  apiResult: ChargingStation
): [ActionStatus | undefined, FriendlyStatus] => {
  const isOffline = useFeatureStore().hasFeature(Feature.ACTIVE_OFFLINE_DETECTION)
    ? apiResult.status === Status.OFFLINE
    : (!apiResult.lastSeen || moment(apiResult.lastSeen) <= moment().subtract(15, 'minutes'));

  const statusesPresent = apiResult.status !== undefined && apiResult.availability !== undefined;

  //Returns Faulted
  if (statusesPresent && apiResult.status === Status.FAULTED) {
    return [ActionStatus.ACTION_REQUIRED, FriendlyStatus.FAULTED];
    //Returns Unknown
  } else if (statusesPresent && apiResult.status == Status.UNKNOWN) {
    return [ActionStatus.ACTION_REQUIRED, FriendlyStatus.UNKNOWN];
    // Returns Offline
  } else if (statusesPresent && isOffline) {
    return [ActionStatus.ACTION_REQUIRED, FriendlyStatus.OFFLINE];
    //Returns Inoperative when not available
  } else if (apiResult.availability === undefined || apiResult.availability === Availability.INOPERATIVE) {
    return [ActionStatus.CONFIGURATION_REQUIRED, FriendlyStatus.INOPERATIVE];
    //Returns Available
  } else if (
    statusesPresent &&
    apiResult.availability === Availability.OPERATIVE &&
    apiResult.status === Status.AVAILABLE
  ) {
    return [ActionStatus.ACTION_REQUIRED, FriendlyStatus.AVAILABLE];
    //Returns No status received (undefined status)
  } else if (apiResult.status === undefined) {
    return [ActionStatus.CONFIGURATION_REQUIRED, FriendlyStatus.NO_STATUS_RECEIVED];
    //Returns Charging
  } else if (statusesPresent && apiResult.status === Status.OCCUPIED && apiResult.subStatus === SubStatus.CHARGING) {
    return [undefined, FriendlyStatus.CHARGING];
    //Returns Suspended_EV
  } else if (
    statusesPresent &&
    apiResult.status === Status.OCCUPIED &&
    apiResult.subStatus === SubStatus.SUSPENDED_EV
  ) {
    return [undefined, FriendlyStatus.SUSPENDED_EV];
    //Returns Occupied
  } else if (statusesPresent && apiResult.status === Status.OCCUPIED) {
    return [undefined, FriendlyStatus.IN_USE];
    //Returns Inoperative (Unavailable)
  } else if (statusesPresent && apiResult.status === Status.INOPERATIVE) {
    return [undefined, FriendlyStatus.INOPERATIVE];
  } else {
    //Returns Idle
    return [undefined, FriendlyStatus.IDLE];
  }
};

export const connectorStatusForConnector = (connector: Connector): EViolinStatus => {
  let connectorIconStatus: EViolinStatus;

  if (
    connector?.evseStatus === EvseStatus.REMOVED ||
    connector?.status === ComponentStatus.UNKNOWN ||
    connector?.status === ComponentStatus.INOPERATIVE ||
    !connector.status
  ) {
    connectorIconStatus = EViolinStatus.INACTIVE;
  } else if (connector?.status === ComponentStatus.FAULTED) {
    connectorIconStatus = EViolinStatus.ERROR;
  } else if (connector?.status === ComponentStatus.AVAILABLE) {
    connectorIconStatus = EViolinStatus.AVAILABLE;
  } else if (
    connector?.status === ComponentStatus.OCCUPIED &&
    connector?.subStatus === ComponentSubStatus.SUSPENDED_EV
  ) {
    connectorIconStatus = EViolinStatus.FULLY_CHARGED;
  } else if (connector?.status === ComponentStatus.OCCUPIED && connector?.subStatus === ComponentSubStatus.CHARGING) {
    connectorIconStatus = EViolinStatus.CHARGING;
  } else {
    connectorIconStatus = EViolinStatus.ACTIVE;
  }
  return connectorIconStatus;
};

export const baseTypeForConnector = (connector: Connector): ConnectorIconType => {
  switch (connector.type) {
    case ConnectorType.C_G105:
      return 'CHAdeMO';
    case ConnectorType.C_CCS_2:
      return 'CCS';
    default:
      return 'TYP2';
  }
};

export const iconForConnector = (connector: Connector): string => {
  const connectorIconStatus: EViolinStatus = connectorStatusForConnector(connector);
  const typeNamePart = baseTypeForConnector(connector);

  return `/connector/${typeNamePart}/Outline/${connectorIconStatus}.svg`;
};

export const backendFilterForActionStatus = (actionStatus: ActionStatus): FilterExpression => {
  switch (actionStatus) {
    case ActionStatus.ACTION_REQUIRED:
      return isNotNull('status')
        .and(isNotNull('availability'))
        .and(
          equals('status', 'FAULTED', true)
            .or(equals('status', 'UNKNOWN', true))
            .or(lessThanOrEqual('lastSeen', `D${moment().utc().subtract(15, 'minutes').toISOString()}`))
        );
    case ActionStatus.CONFIGURATION_REQUIRED:
      return new FilterExpression()
        .not(backendFilterForActionStatus(ActionStatus.ACTION_REQUIRED))
        .and(
          equals('configured', 'false', true)
            .or(equals('accepted', 'false', true))
            .or(equals('availability', 'INOPERATIVE', true))
            .or(isNull('availability'))
            .or(isNull('status'))
        );
  }
};

// TODO: FriendlyStatus can not be queried in the backend
export const backendFilterStatus = (status: FriendlyStatus): FilterExpression => {
  return isNotNull('status').and(equals('status', `${status}`.toUpperCase(), true));
};
